import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Checkbox,
  Stack,
  Button,
  IconButton,
  Menu,
  LinearProgress,
  MenuItem,
  Badge,
} from '@mui/material';
import Tooltip, {tooltipClasses} from '@mui/material/Tooltip';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import moment from '@utils/moment';
import Emitter from '@utils/eventEmitter';
import forPromise from '@utils/forPromise';
import {leftPad} from '@utils/dateUtils';
import Modal from '@views/adminBoard/modal/FormModal';
import DynamicHead from '@components/DynamicHead';

import {
  adminGetBoardContentList,
  adminCreateBoardContent,
  adminUpdateBoardContent,
  adminDeleteBoardContent,
  adminClearBoardContentList,
  adminDownloadBoardAttach,
} from '@store/admin/boardContent';

const HtmlTooltip = styled(({className, ...props}) => (
  <Tooltip {...props} classes={{popper: className}} />
))(({theme}) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#fff',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 400,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
    padding: 10,
  },
}));
const ItemTableCell = styled(TableCell)({
  '&.edit': {
    backgroundColor: '#ffe7e7',
    cursor: 'pointer',
    transition: '.3s background-color',
    '&:hover': {
      backgroundColor: '#fdbdbd',
    }
  }
});
const ButtonGroup = styled(Stack)({
  padding: '10px 0',
  textAlign: 'right'
});
const OptionsButton = styled(IconButton)({
  padding: 0
});
const ContentBox = styled('div')({
  whiteSpace: 'pre-wrap',
  overflow: 'auto',
  maxHeight: 50,
  backgroundColor: '#f9f9f9',
  padding: 10
});

const headerList = [
  {key: '', name: '', checkbox: true, width: '5%'},
  {key: 'name', name: '일자', width: '10%'},
  {key: 'birth', name: '제목', width: '20%'},
  {key: 'companyName', name: '내용' , width: '40%'},
  {key: 'position', name: '파일'},
  {key: '', name: '', width: '5%'},
]

const AdminBoardTable = ({
  boardId,
}) => {
  const dispatch = useDispatch();
  const {
    boardContentList,
  } = useSelector(store => store.boardContentAdmin);
  const [loading, setLoading] = useState(false);
  const [checkeds, setCheckeds] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [modalInfo, setModalInfo] = useState(null);

  const isAllChecked = useMemo(() => {
    if (!boardContentList.length) return false;
    return Object.keys(checkeds).length === boardContentList.length;
  }, [ checkeds, boardContentList ]);

  const onSelectAllClick = () => {
    let updateCheckeds = {}
    if (!isAllChecked) {
      boardContentList.forEach(data => {
        updateCheckeds[data.contentId] = true
      });
    };
    setCheckeds(updateCheckeds);
  };

  const onChangeChecked = (itemId) => {
    let updateCheckeds = Object.assign({}, checkeds);
    if (checkeds[itemId]) {
      updateCheckeds = Object.assign({}, checkeds);
      delete updateCheckeds[itemId];
    } else {
      updateCheckeds[itemId] = true;
    }
    setCheckeds(updateCheckeds);
  };

  const onOpenContentModal = (data) => {
    setModalInfo(data);
    setModalOpen(true);
  };

  const onDeleteBoardContent = (contentId) => {
    Emitter.emit('SHOW_CONFIRM', {
      text: '삭제하시겠습니까?',
      successCb: async () => {
        const response = await dispatch(adminDeleteBoardContent({
          contentId,
        }));
        if (response.payload) {
          onGetBoardContentList();
          Emitter.emit('SHOW_MESSAGE', {
            text: '삭제되었습니다.',
          });
          Emitter.emit('HIDE_CONFIRM');
        }
      },
    });
  };

  const openSelectedDeleteModal = () => {
    const checkedLength = Object.keys(checkeds).length
    if (!checkedLength) {
      Emitter.emit('SHOW_MESSAGE', {
        text: '선택된 게시판이 없습니다.',
        color: 'error'
      })
      return
    }
    Emitter.emit('SHOW_CONFIRM', {
      text: `${checkedLength}개의 게시판를 삭제하시겠습니까?`,
      successCb: () => {
        const checkedList = Object.keys(checkeds);
        forPromise(checkedList, async (contentId, index) => {
          await dispatch(adminDeleteBoardContent({
            contentId,
          }));
          if (index === checkedList.length - 1) {
            setCheckeds({});
            onGetBoardContentList();
            Emitter.emit('SHOW_MESSAGE', {
              text: '삭제되었습니다.',
            });
            Emitter.emit('HIDE_CONFIRM');
          }
        }, 300);
      }
    });
  };

  const onCloseContentModal = () => {
    setModalInfo(null);
    setModalOpen(false);
  };

  const onCompleteContentModal = async ({boardId, contentId, data}) => {
    const apiRequestMethod = contentId ? adminUpdateBoardContent : adminCreateBoardContent;
    const response = await dispatch(apiRequestMethod({boardId, contentId, data}));
    if (response.payload) {
      onCloseContentModal();
      onGetBoardContentList();
      Emitter.emit('SHOW_MESSAGE', {
        text: `${contentId ? '수정' : '등록'}되었습니다.`,
      });
    }
  };

  const onGetBoardContentList = useCallback(async () => {
    if (!boardId) return;
    setLoading(true);
    setCheckeds({});
    await dispatch(adminGetBoardContentList({boardId}));
    setLoading(false);
  }, [dispatch, boardId]);

  useEffect(() => onGetBoardContentList(), [onGetBoardContentList]);
  useEffect(() => {
    return () => {
      dispatch(adminClearBoardContentList());
    };
  }, [dispatch]);

  return (
    <>
      <TableContainer component={Paper} sx={{position: 'relative'}}>
        {loading && <LinearProgress sx={{position: 'absolute', width: '100%'}}/>}
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <DynamicHead 
            list={headerList}
            isAllChecked={isAllChecked}
            onSelectAllClick={onSelectAllClick}
          />
          <TableBody>
            {
              boardContentList && boardContentList.map((item, index) => (
                <BoardTableItem 
                  item={item} 
                  key={index}
                  checked={checkeds[item.contentId]}
                  changeChecked={onChangeChecked}
                  openEditModal={onOpenContentModal}
                  openDeleteModal={onDeleteBoardContent}
                />
              ))
            }
          </TableBody>
        </Table>
      </TableContainer>

      <ButtonGroup 
        direction="row"
        justifyContent="flex-end"
        spacing={1}
      >
        <Button variant="contained" disableElevation onClick={() => onOpenContentModal(null)}>등록</Button>
        <Button variant="outlined" onClick={openSelectedDeleteModal}>삭제</Button>
      </ButtonGroup>

      {modalOpen && <Modal 
        data={modalInfo}
        open={modalOpen}
        boardId={boardId}
        closeModal={onCloseContentModal}
        completeModal={onCompleteContentModal}
      />}
    </>
  )
}

const BoardTableItem = ({
  item,
  checked,
  changeChecked,
  openEditModal,
  openDeleteModal,
  filteredCheckList,
}) => {
  const dispatch = useDispatch()
  const [ anchorEl, setAnchorEl ] = useState(null)
  const open = !!anchorEl

  const handleShowItems = (e) => {
    e.preventDefault()
    setAnchorEl(e.currentTarget)
  }

  const handleCloseItems = () => {
    setAnchorEl(null)
  }

  const handleOpenEditModal = () => {
    setAnchorEl(null)
    openEditModal(item)
  }

  const handleOpenDeleteConfirm = () => {
    setAnchorEl(null)
    openDeleteModal(item.contentId)
  }

  const onDownloadFile = async (data) => {
    const {name, attachId} = data
    dispatch(adminDownloadBoardAttach({
      name,
      attachId,
    }))
  }

  const sortBoardAttachList = useMemo(() => {
    const sortList = [...item.boardAttachResList];
    sortList.sort(function (a, b) {
      if (a.sort > b.sort) {
        return 1;
      }
      if (a.sort < b.sort) {
        return -1;
      }
      // a must be equal to b
      return 0;
    });
    return sortList;
  }, [item]);

  const displayDate = useMemo(() => {
    const {createDateTime} = item;
    return `${createDateTime[0]}-${leftPad(createDateTime[1])}-${leftPad(createDateTime[2])}`;
  }, [item]);

  const isNew = useMemo(() => {
    const startDate = moment(new Date()).subtract(3, 'd');
    const endDate = moment(new Date());
    const targetDate = moment(displayDate);
    const range = moment.range(startDate, endDate);
    return range.contains(targetDate);
  }, [displayDate]);

  return (
    <TableRow sx={{backgroundColor: isNew && '#fffcf6'}}>
      <TableCell padding="checkbox" align="center">
        <Checkbox
          color="primary"
          checked={checked || false}
          onChange={() => changeChecked(item.contentId)}
        />
      </TableCell>
      <ItemTableCell align="center">
        {displayDate}
      </ItemTableCell>
      <ItemTableCell>
        <Stack direction="row" spacing={.5} alignItems="center">
          <div>{item.title}</div>
          {isNew && <FiberNewIcon color={'warning'} />}
        </Stack>
      </ItemTableCell>
      <ItemTableCell>
        <ContentBox>{item.content}</ContentBox>
      </ItemTableCell>
      <ItemTableCell align="center">
        {!!item.boardAttachResList.length && <HtmlTooltip
          placement="top"
          title={
            <>
              {sortBoardAttachList.map((data, index) => {
                return <Tooltip title={data.name} key={index} placement="left">
                  <Button 
                    onClick={() => onDownloadFile(data)}
                    variant="outlined"
                    className="attachFile-btn"
                    sx={{marginBottom: '2px!important', marginTop: '2px!important'}}
                  >{data.name}</Button>
                </Tooltip>
              })}
            </>
          }
        >
          <Badge badgeContent={item.boardAttachResList.length} color="info">
            <Button 
              variant="outlined"
              className="attachFile-btn"
              onClick={() => onDownloadFile(sortBoardAttachList[0])}
            >{sortBoardAttachList[0].name}</Button>
          </Badge>
        </HtmlTooltip>}
      </ItemTableCell>

      <ItemTableCell>
        <OptionsButton component="button" onClick={handleShowItems}>
          <MoreVertIcon />
        </OptionsButton>
        <Menu
          open={open}
          anchorEl={anchorEl}
          onClose={handleCloseItems}
        >
          <MenuItem onClick={handleOpenEditModal}>
            변경
          </MenuItem>
          <MenuItem onClick={handleOpenDeleteConfirm}>
            삭제
          </MenuItem>
        </Menu>
      </ItemTableCell>
    </TableRow>
  )
}

export default AdminBoardTable